import React from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {_openModal} from '../reducers/modals';
import {Button, Checkbox, Column, Dropdown, Icon, LoadingSpinner, Row} from '../index';
import Table from '../components/Table';
import debug from 'debug';
import {gatherData, getUpdatedFilter, parseErrorMessage} from '../utils/utils';
import useQueryObject from '../utils/useQueryObject';
import {isEntitled} from '../reducers/user';
import {withSize} from 'react-sizeme';
import axiosInstance from '../utils/axios';
import useSWR from 'swr';
import useQueryString from '../utils/useQueryString';
import {fixHouseholdType, getInstitutions, getHoldingsTypes} from '../utils/containerUtils';
import {saveAccountSecurities} from '../utils/models';
import {_getConfig} from '../reducers/config';
import {_setNotification} from '../reducers/headerNotification';

const log = debug('circleblack:advisor:analyze:holdings:table');

const HoldingsTable = ({
  sessionRole,
  container,
  openModal,
  size,
  fullPage,
  onRowClick,
  rowsPerPage,
  config,
  getConfig,
  isCashEq,
  setNotification,
  height = '200px',
  maxHeight,
}) => {
  const {data: con} = useSWR(
    container && `/containers/${container.id}`,
    null,
    {fallbackData: container}
  );
  log('con', con);

  const [institutionName] = useQueryString('institutionName');
  const [type] = useQueryString('type');

  React.useEffect(() => {getConfig();}, []);

  const isCashOrMMF = holding => holding.securityID === config.cashSecurityID
    || holding.securitySubtype === 'Money Market Fund';

  const [filters, setFilters] = useQueryObject(
    'holdingTableFilters',
    {
      type,
      institutionName,
    }
  );
  const gatherHoldings = con && gatherData(`/containers/${con.id}/positions`);

  const dataManager = {
    gatherData: inputValue => gatherHoldings({
      ...inputValue,
      ...getUpdatedFilter(filters),
      isCashEq: isEntitled([{group: 'securities', feature: 'isCashEq', access: 'r'}]) ? isCashEq : undefined,
    }),
  };

  React.useEffect(() => {
    if (dataManager.refresh) {
      dataManager.refresh();
    }
  }, [con, filters]);

  const {data: types} = getHoldingsTypes({
    con,
    options: {
      onError: err => openModal({type: 'Error', title: 'Holding Types', err}),
      onSuccess: input => {
        if (type) {
          setFilters(current => ({...current, type: input.find(i => i.label === type)}));
        }
      },
    },
  });

  const {data: institutions} = getInstitutions({
    con,
    options: {
      onError: err => openModal({type: 'Error', title: 'Holding Institutions', err}),
      onSuccess: input => {
        if (institutionName) {
          setFilters(current => ({...current, institutionName: input.find(i => i.label === institutionName)}));
        }
      },
    },
  });

  log('institutions', institutions);

  const holdingInfoIcon = position => <Icon svg={'details'} action={e => {
    e.stopPropagation();
    openModal({
      type: 'PositionInfoModal',
      positionID: position.id,
    });
  }}/>;

  const tppAlertsIcon = position => <Icon svg={'details'} disabled={!position.tpp} action={e => {
    e.stopPropagation();
    openModal({
      type: 'PositionTPPModal',
      positionID: position.id,
    });
  }}/>;

  const doNotSellCheckbox = position => <Checkbox disabled={isCashOrMMF(position) || !position.isAdvisor || !position.securityID}
                                                  dataPoint={{name: 'doNotSell', value: !!position.doNotSell}}
                                                  onChange={(name, value) => saveAccountSecurities(
                                                    position.accountID,
                                                    [{id: position.securityID, [name]: value}],
                                                    {driftMethod: 'JOB', onSuccess: dataManager.refresh}
                                                  )}/>;

  const excludeSecurityButton = position => <Button importance={'secondary'} size={'sm'} label={'Exclude'}
                                                    disabled={!position.securityID
                                                    || !position.isAdvisor
                                                    || isCashOrMMF(position)
                                                    || position.sourceType !== 'AGGREGATOR'}
                                                    action={e => {
                                                      e.stopPropagation();
                                                      openModal({
                                                        type: 'ConfirmModal',
                                                        title: 'Exclude Holding',
                                                        body: 'Excluding this holding removes it from the account and all calculations.',
                                                        button: {
                                                          text: 'Confirm',
                                                          action: () => axiosInstance.put('/accounts/'
                                                            + position.accountID
                                                            + '/securities/'
                                                            + position.securityID
                                                            + '/exclude')
                                                            .then(() => {
                                                              dataManager.refresh();
                                                              setNotification({message: position.name + ' has been excluded.'});
                                                            })
                                                            .catch(err => setNotification({message: parseErrorMessage(err)})),
                                                        },
                                                      });
                                                    }}/>;

  const updateFilters = (name, value) => setFilters({...filters, [name]: value});

  log('rendering');
  const [showAdvanced, setShowAdvanced] = React.useState(false);

  if (!con) {
    return <LoadingSpinner/>;
  }

  return <Column layout={{grow: 1}}>
    {isEntitled([
      {group: 'securities', feature: 'doNotSell', access: 'u'},
      {group: 'securities', feature: 'exclude', access: 'u'},
      {group: 'firms', feature: 'additionalAttributes', access: 'r'},
      {group: 'alerts', feature: 'tpp', access: 'r'},
    ])
    && <Row justifySubRow={'right'}>
      <Button action={() => setShowAdvanced(c => !c)}
              size={'sm'}
              importance={'secondary'}
              label={showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}/>
    </Row>}
    <Table dataManager={dataManager}
           columns={[
             {
               title: 'Holding',
               dataPoint: 'name',
               type: 'text',
               minWidth: 130,
               isSortable: true,
             },
             (fullPage || size.width >= 680) && {
               title: 'Symbol',
               dataPoint: 'ticker',
               type: 'text',
               minWidth: 100,
               isSortable: true,
             },
             (fullPage || size.width >= 790) && {
               title: 'Type',
               dataPoint: 'type',
               type: 'text',
               minWidth: 110,
               isSortable: true,
             },
             (fullPage || size.width >= 350) && container.type !== 'ACCOUNT' && {
               title: 'Account',
               dataPoint: 'accountName',
               type: 'text',
               minWidth: 110,
               isSortable: true,
             },
             (fullPage || size.width >= 900) && ['ADVISOR', 'BRANCH', 'FIRM'].includes(fixHouseholdType(container.type)) && {
               title: 'Household',
               dataPoint: 'householdName',
               type: 'text',
               minWidth: 110,
               isSortable: true,
             },
             (fullPage || size.width >= 1010) && container.type !== 'ACCOUNT' && isEntitled([
               {
                 group: 'account',
                 feature: 'institution',
                 access: 'r',
               },
             ])
             && {
               title: isEntitled([{group: 'account', feature: 'aggrManAccInstName', access: 'r'}]) || sessionRole === 'CUSTOMER'
                 ? 'Institution' : 'Custody',
               dataPoint: isEntitled([{group: 'account', feature: 'aggrManAccInstName', access: 'r'}]) || sessionRole === 'CUSTOMER'
                 ? 'institutionName' : 'accountCustody',
               type: 'text',
               minWidth: 110,
               isSortable: true,
             },
             (fullPage || size.width >= 580) && {
               title: 'Price',
               dataPoint: 'price.amount',
               type: 'currency',
               minWidth: 110,
               isSortable: true,
             },
             (fullPage || size.width >= 460) && {
               title: 'Quantity',
               dataPoint: 'quantity',
               type: 'number',
               minWidth: 110,
               isSortable: true,
             },
             {
               title: 'Market Value',
               dataPoint: 'marketValue.amount',
               type: 'currency',
               minWidth: 120,
               isSortable: true,
             },
             showAdvanced && isEntitled([{group: 'firms', feature: 'additionalAttributes', access: 'r'}]) && {
               title: 'More Info',
               dataPoint: 'id1',
               type: 'custom',
               render: holdingInfoIcon,
               minWidth: 50,
               isSortable: false,
             },
             showAdvanced && isEntitled([{group: 'alerts', feature: 'tpp', access: 'r'}]) && {
               title: 'TPP Alerts',
               dataPoint: 'id2',
               type: 'custom',
               render: tppAlertsIcon,
               minWidth: 50,
               isSortable: false,
             },
             showAdvanced && isEntitled([{group: 'securities', feature: 'doNotSell', access: 'u'}]) && {
               title: 'Do Not Sell',
               dataPoint: 'id3',
               type: 'custom',
               render: doNotSellCheckbox,
               minWidth: 50,
               isSortable: false,
             },
             showAdvanced && isEntitled([{group: 'securities', feature: 'exclude', access: 'u'}]) && {
               title: '',
               dataPoint: 'id4',
               type: 'custom',
               render: excludeSecurityButton,
               minWidth: 50,
               isSortable: false,
             },
           ].filter(c => !!c)}
           defaultSort={{
             column: 'marketValue.amount',
             desc: true,
           }}
           searches={[
             {
               title: 'Holding Name',
               name: 'name',
               isFuzzy: true,
             },
             {
               title: 'Symbol',
               name: 'ticker',
               isFuzzy: true,
             },
             container.type !== 'ACCOUNT' && {
               title: 'Account Name',
               name: 'accountName',
               isFuzzy: true,
             },
             ['ADVISOR', 'BRANCH', 'FIRM'].includes(fixHouseholdType(container.type)) && {
               title: 'Household Name',
               name: 'householdName',
               isFuzzy: true,
             },
           ].filter(s => !!s)}
           rowIdentifier={'id'}
           onRowClick={onRowClick}
           iconsKey={[
             isEntitled([{group: 'securities', feature: 'doNotSell', access: 'u'}]) && {
               image: 'notAllowed', // TODO Reqs Icon for DO NOT SELL
               title: 'Do Not Sell',
               color: '#797B83',
               description: 'The holding is marked as Do Not Sell. The model allocations will be adjusted so that this holding is not sold when rebalancing the account.',
               displayCondition: row => row.doNotSell,
             },
           ].filter(i => !!i)}
           filters={[
             <Dropdown key={'HoldingsTypesFilter'}
                       placeholder={'Select...'}
                       title={'Type'}
                       onChange={updateFilters}
                       isSearchable={false}
                       isClearable={true}
                       options={types}
                       dataPoint={{
                         name: 'type',
                         value: filters.type,
                       }}/>,
             container.type !== 'ACCOUNT' && institutions && <Dropdown key={'HoldingsInstitutionsAndCustodyFilter'}
                                                                       title={isEntitled([
                                                                         {
                                                                           group: 'account',
                                                                           feature: 'aggrManAccInstName',
                                                                           access: 'r',
                                                                         },
                                                                       ]) || sessionRole === 'CUSTOMER' ? 'Institution' : 'Custody'}
                                                                       onChange={updateFilters}
                                                                       isSearchable={true}
                                                                       isClearable={true}
                                                                       options={institutions.map(i => {
                                                                         delete i.subLabel;
                                                                         return i;
                                                                       })}
                                                                       dataPoint={{
                                                                         name: 'institutionName',
                                                                         value: filters.institutionName,
                                                                       }}/>,
           ].filter(f => !!f)}
           rowsPerPage={rowsPerPage}
           filterValues={filters}
           maxHeight={maxHeight}
           height={height}/>
  </Column>;
};

const mapStateToProps = state => ({
  sessionRole: state.session.profile.role,
  config: state.config,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  getConfig: force => dispatch(_getConfig(force)),
  setNotification: notification => dispatch(_setNotification(notification)),
});

export default compose(withSize(), connect(mapStateToProps, mapDispatchToProps))(HoldingsTable);

HoldingsTable.propTypes = {
  sessionRole: PropTypes.shape().isRequired,
  /**
   * SizeMe size
   */
  size: PropTypes.shape({
    width: PropTypes.number,
  }),
  openModal: PropTypes.func.isRequired,
  container: PropTypes.shape({id: PropTypes.number.isRequired, type: PropTypes.string.isRequired}).isRequired,
  config: PropTypes.shape({
    models: PropTypes.shape({driftStatuses: PropTypes.object}),
    cashSecurityID: PropTypes.number,
  }).isRequired,
  getConfig: PropTypes.func.isRequired,
  /**
   * Optional on row click action
   */
  onRowClick: PropTypes.func,
  /**
   * Optional rows per page override
   */
  rowsPerPage: PropTypes.number,
  /**
   * If isEntitled([{group: 'securities', feature: 'isCashEq', access: 'r'} and true/false filter to only cash or non cash positions
   */
  isCashEq: PropTypes.bool,
  setNotification: PropTypes.func,
  /**
   * Indicates this is a full page and should not have size restrictions
   */
  fullPage: PropTypes.bool,
  /**
   * Optional css max height for table
   */
  maxHeight: PropTypes.string,
  /**
   * Optional css height for table
   */
  height: PropTypes.string,
};
